import type { InvoiceStatus, PortalUser, ProjectStatus, TaskStatus } from '../types/portal'

export function formatMoney(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100)
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'Not set'
  return new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const minutes = Math.round(diff / 60_000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`
  return formatDate(iso)
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function fileKind(mimeType: string, filename: string): string {
  if (mimeType === 'application/pdf') return 'PDF'
  if (mimeType.includes('spreadsheet') || /\.(xlsx|csv)$/i.test(filename)) return 'Spreadsheet'
  if (mimeType.includes('wordprocessing') || /\.docx?$/i.test(filename)) return 'Word'
  if (mimeType.includes('presentation') || /\.pptx?$/i.test(filename)) return 'Slides'
  if (mimeType.startsWith('image/')) return 'Image'
  return filename.split('.').pop()?.toUpperCase() ?? 'File'
}

export function displayName(user: Pick<PortalUser, 'firstName' | 'lastName' | 'email'>): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
  return name || user.email
}

export function initials(user: Pick<PortalUser, 'firstName' | 'lastName' | 'email'>): string {
  if (user.firstName) return `${user.firstName[0]}${user.lastName?.[0] ?? ''}`.toUpperCase()
  return user.email[0].toUpperCase()
}

/** Days from today to a date, negative when past. */
export function daysUntil(iso: string): number {
  const target = new Date(iso.length === 10 ? `${iso}T23:59:59Z` : iso).getTime()
  return Math.ceil((target - Date.now()) / 86_400_000)
}

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  archived: 'Archived',
}

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  review: 'In review',
  done: 'Done',
}

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Due',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
}

const AUDIT_LABELS: Record<string, string> = {
  'deliverable.uploaded': 'uploaded',
  'deliverable.downloaded': 'downloaded',
  'deliverable.deleted': 'deleted',
  'member.invited': 'invited',
  'member.removed': 'removed',
  'member.role_changed': 'changed the role of',
  'invoice.sent': 'sent invoice',
  'invoice.paid': 'paid invoice',
  'project.updated': 'updated project',
  'task.deleted': 'deleted task',
}

export function describeAudit(action: string, metadata: Record<string, string> | null): string {
  const verb = AUDIT_LABELS[action] ?? action.replace(/[._]/g, ' ')
  const subject = metadata?.name ?? metadata?.number ?? metadata?.email ?? metadata?.title ?? ''
  const version = metadata?.version && Number(metadata.version) > 1 ? `, version ${metadata.version}` : ''
  const role = action === 'member.role_changed' && metadata ? ` from ${metadata.from} to ${metadata.to}` : ''
  const invitedAs = action === 'member.invited' && metadata?.role ? ` as ${metadata.role}` : ''
  return `${verb} ${subject}${version}${role}${invitedAs}`.trim()
}
